import {
  QueryKey,
  useQueryClient,
  type FetchQueryOptions,
} from '@tanstack/react-query'

import { useServerErrorHandler } from '@/shared/config'

import { ErrorHandler, isHttpError } from './http-error'

export const usePrefetchQuery = () => {
  const queryClient = useQueryClient()
  const { handleServerError } = useServerErrorHandler()

  const prefetch = async <TData, TError, TQueryKey extends QueryKey>(
    options: FetchQueryOptions<TData, TError, TData, TQueryKey>,
    customErrorHandler?: Partial<ErrorHandler>,
  ): Promise<
    { success: true; data: TData } | { success: false; data: null }
  > => {
    try {
      const data = await queryClient.fetchQuery({
        ...options,
        meta: {
          ...options.meta,
          useCustomHandling: true,
        },
      })

      return {
        data,
        success: true,
      }
    } catch (error) {
      if (isHttpError(error)) {
        handleServerError({
          ...error.handler,
          ...customErrorHandler,
        })
      }

      return {
        data: null,
        success: false,
      }
    }
  }

  return { prefetch }
}
